import { v4 as uuid } from 'uuid';
export var OrderChangeType;
(function (OrderChangeType) {
    OrderChangeType["billingAddressChanged"] = "billingAddressChanged";
    OrderChangeType["deliveryAddressChanged"] = "billingAddressChanged";
    OrderChangeType["itemsAdded"] = "itemsAdded";
    OrderChangeType["itemsChanged"] = "itemsChanged";
    OrderChangeType["itemsRemoved"] = "itemsRemoved";
})(OrderChangeType || (OrderChangeType = {}));
export class OrderChange {
    id;
    orderId = null;
    type = null;
    requiresAccounting = false;
    description = null;
    affectedProductId = null;
    changeInQuantity = 0;
    constructor(id, data) {
        this.id = id || uuid();
        if (data) {
            let definedKeys = Object.keys(this).filter((key) => key != "id");
            for (let key in data) {
                let value = data[key];
                let keyToUse = key;
                let keyIsDefinedOnInstance = definedKeys.includes(keyToUse);
                if (keyIsDefinedOnInstance) {
                    if (value) {
                        //replace all firebase dates with real dates
                        if (value.seconds && value.toDate) {
                            value = value.toDate();
                        }
                    }
                    Object.assign(this, { [keyToUse]: value });
                }
            }
        }
    }
}
export class OrderHistoryItem {
    id;
    companyId = null;
    orderId = null;
    date = null;
    changes = [];
    requiresAccounting = false;
    constructor(id, data) {
        this.id = id || uuid();
        if (data) {
            let definedKeys = Object.keys(this).filter((key) => key != "id");
            for (let key in data) {
                let value = data[key];
                let keyToUse = key;
                let keyIsDefinedOnInstance = definedKeys.includes(keyToUse);
                if (keyIsDefinedOnInstance) {
                    if (value) {
                        //replace all firebase dates with real dates
                        if (value.seconds && value.toDate) {
                            value = value.toDate();
                        }
                    }
                    Object.assign(this, { [keyToUse]: value });
                }
            }
        }
    }
    static createFromOrders(oldValue, newValue) {
        if (!oldValue || !newValue || oldValue.id != newValue.id) {
            console.log("OrderHistoryItem:createFromOrders: Invalid Orders", oldValue, newValue);
            throw new Error("OrderHistoryItem:createFromOrders: Invalid Orders");
        }
        let historyItem = new OrderHistoryItem(uuid());
        historyItem.orderId = oldValue.id;
        historyItem.companyId = oldValue.companyId;
        historyItem.date = new Date();
        function compareItems(a, b) {
            let changes = { somethingChanged: false, quantityChanged: false, chosenChanged: false, optionsChanged: false, changesNeedAccounting: false, affectedProductId: null, changeInQuantity: 0 };
            if (a.productId != b.productId) {
                console.error('compareItems: different productIds', a.productId, b.productId);
            }
            else {
                changes.affectedProductId = b.productId;
            }
            if (a.quantity != b.quantity) {
                changes.changeInQuantity = b.quantity - a.quantity;
                changes.quantityChanged = true;
                changes.changesNeedAccounting = true;
                changes.somethingChanged = true;
            }
            //check if any existing options or their chosen values got removed/changed
            if (a.options && a.options.length > 0) {
                for (let option of a.options) {
                    let freshOption = b.options ? b.options.find((el) => option.id == el.id) : null;
                    if (!freshOption) {
                        //the whole option got removed
                        changes.optionsChanged = true;
                        changes.somethingChanged = true;
                        //if a priced value got removed it needs accounting
                        if (option.values && option.values.some((value) => value.correction)) {
                            changes.changesNeedAccounting = true;
                        }
                    }
                    else {
                        //the option is still there: check its chosen values
                        for (let value of option.values || []) {
                            let freshValue = freshOption.values ? freshOption.values.find((el) => value.id == el.id) : null;
                            if (!freshValue) {
                                changes.optionsChanged = true;
                                changes.somethingChanged = true;
                                //if a priced value got removed it needs accounting
                                if (value.correction) {
                                    changes.changesNeedAccounting = true;
                                }
                            }
                            else if (value.correction != freshValue.correction) {
                                throw new Error('prices-changed');
                            }
                        }
                    }
                }
            }
            //check if there are any newly added options or option values
            if (b.options && b.options.length > 0) {
                for (let option of b.options) {
                    let oldOption = a.options ? a.options.find((el) => option.id == el.id) : null;
                    if (!oldOption) {
                        changes.optionsChanged = true;
                        changes.somethingChanged = true;
                        //if a priced value got added it needs accounting
                        if (option.values && option.values.some((value) => value.correction)) {
                            changes.changesNeedAccounting = true;
                        }
                    }
                    else {
                        for (let value of option.values || []) {
                            let oldValue = oldOption.values ? oldOption.values.find((el) => value.id == el.id) : null;
                            if (!oldValue) {
                                changes.optionsChanged = true;
                                changes.somethingChanged = true;
                                //if a priced value got added it needs accounting
                                if (value.correction) {
                                    changes.changesNeedAccounting = true;
                                }
                            }
                        }
                    }
                }
            }
            //check if any chosen subproducts (combined products) got removed or swapped
            if (a.chosen && Object.keys(a.chosen).length > 0) {
                for (let slotId of Object.keys(a.chosen)) {
                    let oldSlot = a.chosen[slotId];
                    let freshSlot = b.chosen ? b.chosen[slotId] : null;
                    if (!freshSlot) {
                        //the slot got removed entirely
                        changes.chosenChanged = true;
                        changes.somethingChanged = true;
                        if (oldSlot.chosen && oldSlot.chosen.correction) {
                            changes.changesNeedAccounting = true;
                        }
                    }
                    else if (oldSlot.chosen.productId != freshSlot.chosen.productId) {
                        //a different subproduct was chosen for this slot
                        changes.chosenChanged = true;
                        changes.somethingChanged = true;
                        //only needs accounting if the price impact of the slot changed
                        if (oldSlot.chosen.correction != freshSlot.chosen.correction) {
                            changes.changesNeedAccounting = true;
                        }
                    }
                    else if (oldSlot.chosen.correction != freshSlot.chosen.correction) {
                        //same subproduct but its price correction changed -> price config changed
                        throw new Error('prices-changed');
                    }
                }
            }
            //check if there are any newly added chosen slots
            if (b.chosen && Object.keys(b.chosen).length > 0) {
                for (let slotId of Object.keys(b.chosen)) {
                    let oldSlot = a.chosen ? a.chosen[slotId] : null;
                    if (!oldSlot) {
                        let freshSlot = b.chosen[slotId];
                        changes.chosenChanged = true;
                        changes.somethingChanged = true;
                        if (freshSlot.chosen && freshSlot.chosen.correction) {
                            changes.changesNeedAccounting = true;
                        }
                    }
                }
            }
            return changes;
        }
        //check addresses
        let billingAddressChanged = JSON.stringify(oldValue.billingAddress) != JSON.stringify(newValue.billingAddress);
        if (billingAddressChanged) {
            historyItem.changes.push(new OrderChange(uuid(), { orderId: oldValue.id, type: OrderChangeType.billingAddressChanged, requiresAccounting: true }));
        }
        let deliveryAddressChanged = JSON.stringify(oldValue.deliveryAddress) != JSON.stringify(newValue.deliveryAddress);
        if (deliveryAddressChanged) {
            historyItem.changes.push(new OrderChange(uuid(), { orderId: oldValue.id, type: OrderChangeType.deliveryAddressChanged, requiresAccounting: true }));
        }
        //check if the existing items changed or got deleted
        for (let item of oldValue.orderItems) {
            let freshItem = newValue.orderItems.find((el) => item.id == el.id);
            //found no corresponding item in the new order: it got deleted
            if (!freshItem) {
                historyItem.changes.push(new OrderChange(uuid(), {
                    orderId: oldValue.id,
                    type: OrderChangeType.itemsRemoved,
                    requiresAccounting: true,
                    affectedProductId: item.productId,
                    changeInQuantity: -1 * item.quantity
                }));
                continue;
            }
            if (freshItem.basePrice != item.basePrice) {
                console.log('freshItem.basePrice != item.basePrice', freshItem.basePrice, item.basePrice);
                throw new Error('prices-changed');
            }
            let changes = compareItems(item, freshItem);
            if (changes.somethingChanged) {
                historyItem.changes.push(new OrderChange(uuid(), {
                    orderId: oldValue.id,
                    type: OrderChangeType.itemsChanged,
                    requiresAccounting: changes.changesNeedAccounting,
                    affectedProductId: changes.affectedProductId,
                    changeInQuantity: changes.changeInQuantity
                }));
            }
        }
        // console.log('oldValues',oldValue.orderItems.map((el) => {
        //     return el.id
        // }))
        // console.log('newValues',newValue.orderItems.map((el) => {
        //     return el.id
        // }))
        //check if there are new items which arent in th old order        
        for (let item of newValue.orderItems) {
            let oldItem = oldValue.orderItems.find((el) => item.id == el.id);
            if (!oldItem) {
                historyItem.changes.push(new OrderChange(uuid(), {
                    orderId: oldValue.id,
                    type: OrderChangeType.itemsAdded,
                    requiresAccounting: true,
                    affectedProductId: item.productId,
                    changeInQuantity: item.quantity
                }));
                continue;
            }
        }
        return historyItem;
    }
}
